import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useTheme } from '../context/ThemeContext';
import PortalLayout from '../components/PortalLayout';
import './RiskAnalysis.css'; // Reuse the same styles

// Get the main platform URL based on environment
const getMainPlatformUrl = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:3000';
  }
  return 'https://ai.app.platform.halisoft.biz';
};

const InvoiceFinancing = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [user, setUser] = useState(null);
  const [quota, setQuota] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuotaAlert, setShowQuotaAlert] = useState(false);

  // Module-specific config
  const moduleConfig = {
    name: 'HaliVerify',
    title: 'Invoice Eligibility Checker',
    description: 'AI-powered invoice verification and financing eligibility analysis',
    icon: '📋',
    color: '#6366f1', // Indigo
    iframePath: '/customer/factor-tools/invoice-financing-check', // Path on main platform
    moduleId: 'invoice-financing-check',
    quotaUnit: 'analyses',
    warningThreshold: 80,
    criticalThreshold: 95
  };

  // Theme-aware colors
  const colors = {
    cardBg: isDark ? '#1e293b' : 'white',
    textPrimary: isDark ? '#f3f4f6' : '#1f2937',
    textSecondary: isDark ? '#9ca3af' : '#6b7280',
    textMuted: isDark ? '#6b7280' : '#4b5563',
    border: isDark ? '#334155' : '#e5e7eb',
    bgMuted: isDark ? '#0f172a' : '#f3f4f6'
  };

  const loadUserData = useCallback(async () => {
    try {
      // Check URL parameters first (passed from main platform)
      const urlParams = new URLSearchParams(window.location.search);
      const urlEmail = urlParams.get('email');
      const urlToken = urlParams.get('token');

      // If auth comes from URL, store it for future use
      if (urlEmail) {
        localStorage.setItem('halisoft_email', urlEmail);
        console.log('[InvoiceFinancing] Auth received from URL:', urlEmail);
      }
      if (urlToken) {
        localStorage.setItem('halisoft_token', urlToken);
      }

      // Check if user is authenticated - check localStorage
      const token = localStorage.getItem('halisoft_token');
      const storedUser = localStorage.getItem('halisoft_user');
      const storedEmail = localStorage.getItem('halisoft_email');

      // Use URL email first, then localStorage
      let email = urlEmail || storedEmail;
      if (!email && storedUser) {
        try {
          email = JSON.parse(storedUser)?.email;
        } catch (e) { /* ignore */ }
      }

      // If no authentication at all, redirect to login
      if (!token && !storedUser && !storedEmail && !urlEmail) {
        console.log('[InvoiceFinancing] No authentication found, redirecting to login');
        navigate('/login?redirect=/invoice-financing');
        return;
      }

      // Try to get user from API
      const userRes = await api.get('/api/user/me').catch((err) => {
        // If 401 but we have email from URL, continue with that
        if (err.response?.status === 401 && email) {
          return { data: { email } };
        }
        if (err.response?.status === 401) {
          throw err;
        }
        // Otherwise use stored email
        return { data: { email: email || 'Guest' } };
      });

      const userEmail = userRes.data?.email || email;
      if (!userEmail || userEmail === 'Guest') {
        console.log('[InvoiceFinancing] No valid email, redirecting to login');
        navigate('/login?redirect=/invoice-financing');
        return;
      }

      // Module-specific: InvoiceFinancing has its own plan/quota
      const moduleParam = moduleConfig.moduleId;
      const [quotaRes, subRes] = await Promise.all([
        api.get(`/api/user/quota?email=${encodeURIComponent(userEmail)}&module=${encodeURIComponent(moduleParam)}`),
        api.get(`/api/subscriptions/current?email=${encodeURIComponent(userEmail)}&module=${encodeURIComponent(moduleParam)}`).catch(() => ({ data: null }))
      ]);

      setUser(userRes.data);
      setSubscription(subRes.data);
      setQuota(quotaRes.data);

      // Show alert if quota is low
      if (quotaRes.data.percentageUsed >= moduleConfig.warningThreshold) {
        setShowQuotaAlert(true);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      if (err.response?.status === 401) {
        // Clear stale data and redirect to login
        localStorage.removeItem('halisoft_token');
        localStorage.removeItem('halisoft_user');
        localStorage.removeItem('halisoft_email');
        navigate('/login?redirect=/invoice-financing');
        return;
      }
      // For other errors, show error state
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [navigate, moduleConfig.warningThreshold, moduleConfig.moduleId]);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  const handleLogout = async () => {
    try {
      await api.post('/api/auth/logout');
      localStorage.removeItem('halisoft_user');
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  if (loading) {
    return (
      <PortalLayout>
        <div className="risk-analysis-loading">
          <div className="spinner-large"></div>
          <p>Loading...</p>
        </div>
      </PortalLayout>
    );
  }

  const quotaPercentage = quota ? (quota.used / quota.limit) * 100 : 0;
  const isQuotaLow = quotaPercentage >= moduleConfig.warningThreshold;
  const isQuotaCritical = quotaPercentage >= moduleConfig.criticalThreshold;
  const mainPlatformUrl = getMainPlatformUrl();

  return (
    <PortalLayout>
      <div className="risk-analysis-page" style={{ paddingTop: 0 }}>
        {/* Page Header */}
        <div style={{
          background: `linear-gradient(135deg, ${moduleConfig.color}20, ${moduleConfig.color}05)`,
          borderRadius: '16px',
          padding: '1.5rem',
          marginBottom: '1.5rem',
          border: isDark ? `1px solid ${moduleConfig.color}40` : `1px solid ${moduleConfig.color}30`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '14px',
              background: `linear-gradient(135deg, ${moduleConfig.color}, ${moduleConfig.color}dd)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.75rem',
              boxShadow: `0 4px 12px ${moduleConfig.color}40`
            }}>
              {moduleConfig.icon}
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700', color: colors.textPrimary }}>
                {moduleConfig.name}
              </h1>
              <p style={{ margin: '0.25rem 0 0', color: colors.textSecondary, fontSize: '0.95rem' }}>
                {moduleConfig.title}
              </p>
            </div>
          </div>
        </div>

        {/* Quota Alert Banner */}
      {showQuotaAlert && quota.remaining > 0 && (
        <div className={`quota-alert-banner ${isQuotaCritical ? 'critical' : 'warning'}`}>
          <span className="alert-icon">
            {isQuotaCritical ? '🚨' : '⚠️'}
          </span>
          <span className="alert-text">
            {isQuotaCritical
              ? `Warning! You only have ${quota.remaining} ${moduleConfig.quotaUnit} left this month.`
              : `Low quota: ${quota.remaining} ${moduleConfig.quotaUnit} remaining this month.`}
          </span>
          <button
            onClick={() => navigate(`/subscription?module=${moduleConfig.moduleId}`)}
            className="alert-upgrade-btn"
          >
            ⚡ Upgrade
          </button>
          <button
            onClick={() => setShowQuotaAlert(false)}
            className="alert-close-btn"
          >
            ✕
          </button>
        </div>
      )}

      {/* Quota Exceeded Banner */}
      {quota && quota.remaining === 0 && (
        <div className="quota-exceeded-banner">
          <div className="exceeded-content">
            <span className="exceeded-icon">🚫</span>
            <div className="exceeded-text">
              <h3>Quota Exhausted</h3>
              <p>
                You have used all your {quota.limit} {moduleConfig.quotaUnit} this month.
                Upgrade to a higher plan to continue.
              </p>
            </div>
            <button
              onClick={() => navigate(`/subscription?module=${moduleConfig.moduleId}`)}
              className="exceeded-upgrade-btn"
            >
              🚀 View Plans
            </button>
          </div>
        </div>
      )}

      {/* Compact Quota & Plan Info Bar */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '1rem',
        flexWrap: 'wrap'
      }}>
        {/* Quota Card */}
        <div style={{
          flex: '1 1 200px',
          background: isDark ? '#1e293b' : 'white',
          borderRadius: '12px',
          padding: '1rem 1.25rem',
          border: isDark ? '1px solid #334155' : '1px solid #e5e7eb',
          boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.08)',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '10px',
            background: isDark ? `${moduleConfig.color}25` : `${moduleConfig.color}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.5rem'
          }}>
            {moduleConfig.icon}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.8rem', color: isDark ? '#9ca3af' : '#6b7280', marginBottom: '0.25rem' }}>Monthly Quota</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.25rem' }}>
              <span style={{ fontSize: '1.5rem', fontWeight: '700', color: moduleConfig.color }}>{quota?.remaining || 0}</span>
              <span style={{ fontSize: '0.9rem', color: isDark ? '#6b7280' : '#4b5563' }}>/ {quota?.limit || 0} {moduleConfig.quotaUnit}</span>
            </div>
            <div style={{ height: '4px', background: isDark ? '#334155' : '#e5e7eb', borderRadius: '2px', marginTop: '0.5rem', overflow: 'hidden' }}>
              <div style={{
                width: `${Math.min(quotaPercentage, 100)}%`,
                height: '100%',
                background: isQuotaCritical ? '#ef4444' : isQuotaLow ? '#f59e0b' : moduleConfig.color,
                borderRadius: '2px'
              }} />
            </div>
          </div>
        </div>

        {/* Plan Card */}
        <div style={{
          flex: '1 1 200px',
          background: isDark ? '#1e293b' : 'white',
          borderRadius: '12px',
          padding: '1rem 1.25rem',
          border: isDark ? '1px solid #334155' : '1px solid #e5e7eb',
          boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem'
        }}>
          <div>
            <div style={{ fontSize: '0.8rem', color: isDark ? '#9ca3af' : '#6b7280', marginBottom: '0.25rem' }}>Current Plan</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '700', color: isDark ? '#f3f4f6' : '#1f2937' }}>{subscription?.name || quota?.plan || 'FREE'}</div>
            <div style={{ fontSize: '0.85rem', color: isDark ? '#6b7280' : '#4b5563' }}>
              {subscription?.slug === 'enterprise' ? 'Customised' :
               parseFloat(subscription?.price) > 0 ? `$${parseFloat(subscription.price).toFixed(2)}/mo` : 'Free'}
            </div>
          </div>
          <button
            onClick={() => {
              const email = user?.email || localStorage.getItem('halisoft_email') || '';
              navigate(`/subscription?module=${moduleConfig.moduleId}${email ? `&email=${encodeURIComponent(email)}` : ''}`);
            }}
            style={{
              padding: '0.6rem 1rem',
              background: moduleConfig.color,
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '0.85rem',
              whiteSpace: 'nowrap'
            }}
          >
            ⬆️ Upgrade
          </button>
        </div>
      </div>

      {/* Main Content - iFrame */}
      <div style={{
        background: isDark ? '#1e293b' : 'white',
        borderRadius: '16px',
        border: isDark ? '1px solid #334155' : '1px solid #e5e7eb',
        boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        height: 'calc(100vh - 340px)',
        minHeight: '400px'
      }}>
        {quota && quota.remaining > 0 ? (
          <iframe
            src={`${mainPlatformUrl}${moduleConfig.iframePath}?email=${encodeURIComponent(user?.email || localStorage.getItem('halisoft_email') || '')}`}
            title={moduleConfig.name}
            style={{
              width: '100%',
              height: '100%',
              border: 'none'
            }}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            allow="clipboard-write"
          />
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            padding: '2rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🚫</div>
            <h2 style={{ color: isDark ? '#f3f4f6' : '#1f2937', marginBottom: '0.5rem' }}>Service Blocked</h2>
            <p style={{ color: isDark ? '#9ca3af' : '#6b7280', marginBottom: '1rem', maxWidth: '400px' }}>
              You have exhausted your monthly quota. Upgrade your plan to continue using {moduleConfig.name}.
            </p>
            <p style={{ color: isDark ? '#6b7280' : '#4b5563', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
              Your quota will reset on the 1st of next month.
            </p>
            <button
              onClick={() => {
                const email = user?.email || localStorage.getItem('halisoft_email') || '';
                navigate(`/subscription?module=${moduleConfig.moduleId}${email ? `&email=${encodeURIComponent(email)}` : ''}`);
              }}
              style={{
                padding: '0.85rem 2rem',
                background: moduleConfig.color,
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontWeight: '700',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              🚀 Upgrade Now
            </button>
          </div>
        )}
      </div>

      {/* Floating Chat Badge - Opens HaliGuide on main platform */}
      <button
        onClick={() => {
          const width = 420;
          const height = 700;
          const left = window.screenX + 50;
          const top = window.screenY + 100;
          window.open(
            `${mainPlatformUrl}/landing`,
            'HaliGuide',
            `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
          );
        }}
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          zIndex: 1500,
          background: `linear-gradient(135deg, ${moduleConfig.color}90, ${moduleConfig.color})`,
          border: 'none',
          padding: '14px 20px',
          borderRadius: '50px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          boxShadow: `0 8px 32px ${moduleConfig.color}50`,
          transition: 'all 0.3s ease',
          color: 'white',
          fontWeight: '600',
          fontSize: '0.95rem'
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = 'scale(1.05)';
          e.currentTarget.style.boxShadow = `0 12px 40px ${moduleConfig.color}60`;
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = `0 8px 32px ${moduleConfig.color}50`;
        }}
        title="Chat with HaliGuide"
      >
        <span style={{ fontSize: '1.2rem' }}>💬</span>
        <span>Need Help?</span>
      </button>
      </div>
    </PortalLayout>
  );
};

export default InvoiceFinancing;
